import React, { useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, useLocation } from 'react-router-dom';
import { translations } from './translations';
import Home from './pages/Home';
import Admin from './pages/Admin';
import About from './pages/About';
import Gallery from './pages/Gallery';
import AcousticsGuide from './pages/AcousticsGuide';
import FloatingCall from './components/FloatingCall';
import CookieConsent from './components/CookieConsent';
import AccessibilityToolbar from './components/AccessibilityToolbar';
import WindowCalculator from './Window1700Calculator';
import Window9000Calculator from './Window9000Calculator';
import Window7000Calculator from './Window7000Calculator';
import GlassWeightCalculator from './GlassWeightCalculator';
import ShutterWeightCalculator from './ShutterWeightCalculator';
import GlassOrder from './GlassOrder';
import JobApproval from './JobApproval';
import NewProject from './NewProject';
import Professionals from './pages/Professionals';
import DailyTaskReport from './pages/DailyTaskReport';
import OfficeRental from './OfficeRental';
import CraneService from './CraneService';
import OfficeFlyer from './OfficeFlyer';
import ProfilesCatalog from './pages/ProfilesCatalog';
import PerzolCatalog from './pages/PerzolCatalog';
import GlassCatalog from './pages/GlassCatalog';
import ProfProjectsList from './pages/ProfProjectsList';
import ProfProjectDetail from './pages/ProfProjectDetail';
import { AuthProvider, useAuth } from './context/AuthContext';
import Login from './pages/Login';
import Signup from './pages/Signup';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import VerifyEmail from './pages/VerifyEmail';
import { Navigate } from 'react-router-dom';

const ProtectedRoute = ({ children }) => {
    const { currentUser, loading } = useAuth();
    if (loading) return <div className="min-h-screen flex items-center justify-center font-bold">טוען...</div>;
    return currentUser ? children : <Navigate to="/login" replace />;
};

function AppContent() {
    const [lang, setLang] = useState('en');
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const t = translations[lang];
    const location = useLocation();
    const hideHeader = location.pathname === '/office' || location.pathname === '/admin';
    const { currentUser, logout } = useAuth();

    // Close mobile menu on route change
    React.useEffect(() => { setMobileMenuOpen(false); }, [location.pathname]);

    return (
        <div dir={t.dir} className="min-h-screen bg-white font-sans text-slate-900">
            {/* --- HEADER (hidden on /office) --- */}
            {!hideHeader && (
                <header className="bg-white/95 backdrop-blur-md shadow-sm sticky top-0 z-[60] border-b">
                    <div className="container mx-auto px-6 py-2 flex justify-between items-center">
                        <Link to="/" className="flex items-center py-1">
                            <img
                                src="/assets/logo-full.png"
                                alt="Alum Nawatha LTD"
                                width="240"
                                height="96"
                                className="h-16 md:h-24 w-auto object-contain transition-transform duration-300 hover:scale-[1.02]"
                            />
                        </Link>

                        <div className="flex items-center gap-4 lg:gap-10">
                            {/* Desktop Nav */}
                            <nav className="hidden lg:flex gap-8 font-bold text-sm uppercase tracking-widest text-slate-600 items-center">
                                <Link to="/" className="hover:text-blue-900 transition-colors">{t.home}</Link>
                                <Link to="/about" className="hover:text-blue-900 transition-colors">{t.about}</Link>
                                <Link to="/gallery" className="hover:text-blue-900 transition-colors">{t.gallery}</Link>
                                <Link to="/acoustics-guide" className="hover:text-blue-900 transition-colors">{t.acoustics || 'אקוסטיקה'}</Link>
                                <a href="/#contact" className="hover:text-blue-900 transition-colors">{t.contact}</a>
                                
                                {currentUser ? (
                                    <>
                                        <Link to="/professionals" className="text-blue-600 hover:text-blue-900 transition-colors">אזור מקצועי</Link>
                                        {currentUser.role === 'admin' && (
                                            <Link to="/admin" className="text-amber-600 hover:text-amber-800 transition-colors flex items-center gap-1">
                                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.066 2.573c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.573 1.066c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.066-2.573c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" /><path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
                                                ניהול
                                            </Link>
                                        )}
                                        <button onClick={logout} className="bg-red-50 text-red-600 px-4 py-1.5 rounded-full text-xs font-bold hover:bg-red-100 transition">התנתק</button>
                                    </>
                                ) : (
                                    <Link to="/login" className="bg-slate-900 text-white px-5 py-2 rounded-full text-xs font-bold hover:bg-blue-600 transition">התחברות בעלי מקצוע</Link>
                                )}
                            </nav>

                            <select
                                value={lang}
                                onChange={(e) => setLang(e.target.value)}
                                className="bg-slate-100 border-none rounded-full px-4 py-2 font-bold text-xs outline-none cursor-pointer focus:ring-2 focus:ring-blue-500/20 transition-all"
                            >
                                <option value="en">ENGLISH</option>
                                <option value="he">עברית</option>
                                <option value="ar">العربية</option>
                            </select>

                            {/* Mobile Hamburger Button */}
                            <button
                                className="lg:hidden flex flex-col justify-center items-center w-10 h-10 rounded-lg hover:bg-slate-100 transition-colors"
                                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                                aria-label="Toggle menu"
                            >
                                {mobileMenuOpen ? (
                                    <span className="text-2xl font-bold text-slate-700">✕</span>
                                ) : (
                                    <>
                                        <span className="block w-6 h-0.5 bg-slate-700 mb-1.5"></span>
                                        <span className="block w-6 h-0.5 bg-slate-700 mb-1.5"></span>
                                        <span className="block w-6 h-0.5 bg-slate-700"></span>
                                    </>
                                )}
                            </button>
                        </div>
                    </div>

                    {/* Mobile Nav Dropdown */}
                    {mobileMenuOpen && (
                        <nav className="lg:hidden border-t border-slate-200 bg-white shadow-lg animate-fadeIn">
                            <div className="container mx-auto px-6 py-4 flex flex-col gap-3">
                                <Link to="/" className="py-2 px-3 rounded-lg font-bold text-slate-700 hover:bg-blue-50 hover:text-blue-900 transition-colors">{t.home}</Link>
                                <Link to="/about" className="py-2 px-3 rounded-lg font-bold text-slate-700 hover:bg-blue-50 hover:text-blue-900 transition-colors">{t.about}</Link>
                                <Link to="/gallery" className="py-2 px-3 rounded-lg font-bold text-slate-700 hover:bg-blue-50 hover:text-blue-900 transition-colors">{t.gallery}</Link>
                                <Link to="/acoustics-guide" className="py-2 px-3 rounded-lg font-bold text-slate-700 hover:bg-blue-50 hover:text-blue-900 transition-colors">{t.acoustics || 'אקוסטיקה'}</Link>
                                <a href="/#contact" className="py-2 px-3 rounded-lg font-bold text-slate-700 hover:bg-blue-50 hover:text-blue-900 transition-colors">{t.contact}</a>
                                
                                <div className="border-t border-slate-100 my-2"></div>
                                
                                {currentUser ? (
                                    <>
                                        <Link to="/professionals" className="py-2 px-3 rounded-lg font-bold text-blue-600 hover:bg-blue-50 transition-colors">אזור בעלי מקצוע</Link>
                                        {currentUser.role === 'admin' && (
                                            <Link to="/admin" className="py-2 px-3 rounded-lg font-bold text-amber-600 hover:bg-amber-50 transition-colors flex items-center gap-2">
                                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.066 2.573c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.573 1.066c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.066-2.573c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" /><path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
                                                לוח ניהול
                                            </Link>
                                        )}
                                        <button onClick={logout} className="py-2 px-3 rounded-lg font-bold text-red-600 text-right hover:bg-red-50 transition-colors">התנתקות</button>
                                    </>
                                ) : (
                                    <Link to="/login" className="py-2 px-3 bg-slate-900 text-white text-center rounded-lg font-bold hover:bg-blue-600 transition-colors">התחברות לאזור המקצועי</Link>
                                )}
                            </div>
                        </nav>
                    )}
                </header>
            )}

            {/* --- MAIN PAGE CONTENT --- */}
            <main>
                <Routes>
                    <Route path="/" element={<Home lang={lang} t={t} />} />
                    <Route path="/about" element={<About lang={lang} t={t} />} />
                    <Route path="/gallery" element={<Gallery lang={lang} t={t} />} />
                    <Route path="/admin" element={<Admin />} />
                    
                    {/* Public Auth Routes */}
                    <Route path="/login" element={<Login />} />
                    <Route path="/signup" element={<Signup />} />
                    <Route path="/forgot-password" element={<ForgotPassword />} />
                    <Route path="/reset-password/:token" element={<ResetPassword />} />
                    <Route path="/verify/:token" element={<VerifyEmail />} />
                    
                    {/* Protected Professional Content */}
                    <Route path="/1700" element={<ProtectedRoute><WindowCalculator /></ProtectedRoute>} />
                    <Route path="/9000" element={<ProtectedRoute><Window9000Calculator /></ProtectedRoute>} />
                    <Route path="/7000" element={<ProtectedRoute><Window7000Calculator /></ProtectedRoute>} />
                    <Route path="/glass-weight" element={<ProtectedRoute><GlassWeightCalculator /></ProtectedRoute>} />
                    <Route path="/shutter-weight" element={<ProtectedRoute><ShutterWeightCalculator /></ProtectedRoute>} />
                    <Route path="/professionals" element={<ProtectedRoute><Professionals /></ProtectedRoute>} />
                    <Route path="/profiles-catalog" element={<ProtectedRoute><ProfilesCatalog /></ProtectedRoute>} />
                    <Route path="/perzol-catalog" element={<ProtectedRoute><PerzolCatalog /></ProtectedRoute>} />
                    <Route path="/glass-catalog" element={<ProtectedRoute><GlassCatalog /></ProtectedRoute>} />
                    <Route path="/glass-order" element={<ProtectedRoute><GlassOrder /></ProtectedRoute>} />
                    <Route path="/professional/projects" element={<ProtectedRoute><ProfProjectsList /></ProtectedRoute>} />
                    <Route path="/professional/projects/:id" element={<ProtectedRoute><ProfProjectDetail /></ProtectedRoute>} />
                    <Route path="/job-approval" element={<ProtectedRoute><JobApproval /></ProtectedRoute>} />
                    <Route path="/new-project" element={<ProtectedRoute><NewProject /></ProtectedRoute>} />
                    <Route path="/daily-task-report" element={<ProtectedRoute><DailyTaskReport /></ProtectedRoute>} />
                    
                    {/* Unprotected Form Completion For JobApproval via Token doesn't require Auth because the token grants access */}
                    <Route path="/job-approval/:token" element={<JobApproval />} />
                    <Route path="/office" element={<OfficeRental />} />
                    <Route path="/flyer" element={<OfficeFlyer />} />
                    <Route path="/crane-service" element={<CraneService />} />
                    <Route path="/crane" element={<CraneService />} />
                    <Route path="/acoustics-guide" element={<AcousticsGuide lang={lang} t={t} />} />
                </Routes>
            </main>

            {/* --- FLOATING COMPONENTS --- */}
            <FloatingCall t={t} />
            <CookieConsent />
            <AccessibilityToolbar />

            {/* --- FOOTER (hidden on /office) --- */}
            {!hideHeader && (
                <footer className="bg-slate-950 text-white py-16 border-t border-slate-800">
                    <div className="container mx-auto px-6 text-center">
                        <img
                            src="/assets/logo-circle.png"
                            loading="lazy"
                            width="64"
                            height="64"
                            className="w-16 h-16 mx-auto mb-6 grayscale opacity-40 hover:opacity-100 transition-opacity duration-500"
                            alt="Footer Branding"
                        />
                        <div className="text-2xl font-black mb-3 uppercase italic tracking-tighter">
                            ALUM NAWATHA LTD
                        </div>
                        <div className="flex justify-center items-center gap-4 mb-4">
                            <div className="h-[1px] w-8 bg-slate-700"></div>
                            <p className="text-slate-500 text-[10px] uppercase tracking-[0.4em] font-bold">
                                {t.experience}
                            </p>
                            <div className="h-[1px] w-8 bg-slate-700"></div>
                        </div>
                        <Link to="/professionals" className="inline-flex items-center gap-2 mt-6 mb-4 bg-slate-800 hover:bg-slate-700 text-slate-300 hover:text-white px-5 py-2.5 rounded-full text-xs font-bold uppercase tracking-widest transition-all duration-300 border border-slate-700 hover:border-slate-500">
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.066 2.573c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.573 1.066c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.066-2.573c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                                <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                            </svg>
                            Professional Tools
                        </Link>
                        <p className="text-slate-600 text-[9px] uppercase tracking-widest mt-4 opacity-50">
                            &copy; {new Date().getFullYear()} Alum Nawatha Aluminum Industry. All Rights Reserved.
                        </p>
                    </div>
                </footer>
            )}
        </div>
    );
}

function App() {
    return (
        <AuthProvider>
            <Router>
                <AppContent />
            </Router>
        </AuthProvider>
    );
}

export default App;